import { GenericTabView } from "./generic-tab";
import { ReportDetailModel } from "./../../models/report-detail";
import { AuthorizationModel } from "./../../../authentication/models/authorization-model";
import { ReportTabModel } from "./../../models/tabs/report";
import { AppSettings } from "./../../../app-settings";
import { transient, Container } from "aurelia-dependency-injection";
import { backboneViewProperties } from "../../../decorators/backbone-properties";
import "jquery.scrollto";
import "../../../shared/plugins/highlight";
import "waypoints/lib/jquery.waypoints";
import "jquery-ui/themes/base/core.css";
import "jquery-ui/themes/base/sortable.css";
import "jquery-ui/ui/widgets/sortable";
import "jquery-ui/ui/effect";
import { FullTextMode } from "../../models/document-state";
import { CaseDocumentModel } from "../../models/case-document";
import { EUDocumentModel } from "../../models/eu-document";
import { AppEvents } from "../../../app-events";
import { AuthService } from "../../../authentication/auth-service";
import { ContainerHelper } from "../../../shared/helpers/container-helper";
import { TermSanitiser } from "../../helpers/search-term-sanitiser";

const restrictedReport = require<string>("../partials/restricted-report.html");
const reportHeader = require<string>("../partials/report-header.html");

const template = require<string>("./report.html");
const dom = {
    FULLSCREEN_READER_BUTTON: ".fullscreen-reader-button",
    FULLSCREEN_READER_NEW_TAB_BUTTON: "#read-mode-open-new-tab",
    REPORT_PDF_BUTTON: ".report-button",
    DOCUMENT_PDF_BUTTON: ".document-pdf-button",
    HIGHLIGHT_BUTTON: ".highlight-button",
    HIGHLIGHT_CONTROLS: ".highlight-controls",
    PARAGRAPH_CONTROLS: ".paragraph-controls",
    SEARCH_TERM_PREV: ".search-term-up",
    SEARCH_TERM_NEXT: ".search-term-down",
    FULL_TEXT_CONTENT: ".report-full-text-content",
    HIGHLIGHTED_TEXT: ".text-highlight",
    CURRENT_HIGHLIGHTED_TERM: ".current-text-highlight-term",
    TOGGLE_HEADER_CONTENT: ".toggle-header-content",
    REPORT_HOLDER: ".report-holder",
    HEADER_CONTENT_CONTROLS: ".header-content-controls"
};

@transient()
@backboneViewProperties({
    template: template
})
export class ReportTabView extends GenericTabView {

    public newTab: Window;
    public model: ReportTabModel;
    private appEvents: Backbone.Events;
    private authService: AuthService;
    private currentSource: string;
    private currentIndex: number;
    private fullTextType: string;

    public constructor(options?: any) {
        super(options);
        this.appEvents = ContainerHelper.Events;
        this.authService = ContainerHelper.AuthService;
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.REPORT_PDF_BUTTON] = (e) => this.reportSelectClick(e);
        events["click " + dom.FULLSCREEN_READER_BUTTON] = (e) => this.fullscreenReaderButtonClick(e);
        events["contextmenu " + dom.FULLSCREEN_READER_BUTTON] = (e) => {
            e.preventDefault();
            this.toggleReadModeDropdown();
        };
        events["click " + dom.FULLSCREEN_READER_NEW_TAB_BUTTON] = (e) => {
            this.toggleReadModeDropdown();
            this.fullscreenReaderNewTabButtonClick(e);
        };
        events["click " + dom.DOCUMENT_PDF_BUTTON] = (e) => this.documentPDFClick(e);
        events["click " + dom.HIGHLIGHT_BUTTON] = (e) => this.highlightClick(e);
        events["click " + dom.SEARCH_TERM_PREV] = (e) => this.searchTermPrevClick(e);
        events["click " + dom.SEARCH_TERM_NEXT] = (e) => this.searchTermNextClick(e);
        events["click " + dom.TOGGLE_HEADER_CONTENT] = () => this.toggleHeaderContent();
        return events;
    }

    public setupModelListeners() {
        this.listenTo(this.model, "change:Active", this.activeTabChanged);
        this.listenTo(this.model.State, "change:FullTextMode", this.fullTextModeChanged);
        this.listenTo(this.model.State, "change:Highlighting", this.highlightingChanged);
    }

    public onShow() {
        this.setupModelListeners();
        this.model.State.FullTextMode = FullTextMode.Text;
        this.currentSource = this.model.ReportDetails[0].Source;
        this.currentIndex = 0;

        window.addEventListener("message", (e) => this.receieveMessage(e), false);
    }

    private loadReportText() {
        const reportSource = this.currentSource;
        const index = this.currentIndex;
        return new Promise((resolve, reject) => {
            if (_.contains(this.authService.AppUser.Indicators, reportSource)) {
                let docId = "";
                if (this.model.Data.RenderType.toLowerCase() === "case") {
                    const docModel = this.model.Data as CaseDocumentModel;
                    docId = docModel.EntityId;
                } else { // We're only allowing cases and eu
                    const docModel = this.model.Data as EUDocumentModel;
                    docId = docModel.CelexNumber;
                }

                this.loadCSS(`assets/css/reports/${reportSource}.css`);
                this.model.getReportFulltextFromServer(reportSource, docId, index).then((response) => {
                    this.model.State.Highlighting = false;
                    this.unloadReportHolder();
                    const reportFullText = $(response.FullText);
                    if (this.model.ReportDetails.length > 1) {
                        const currentCitation = reportFullText.find(".citation").text() || "N/A";
                        const reportHeaderWithCitation = Mustache.render(reportHeader, {Citation: currentCitation});
                        this.loadReportHolder(reportFullText.prepend(reportHeaderWithCitation));
                    } else {
                        this.loadReportHolder(reportFullText);
                    }
                    this.setButtonStatusForTextDisplay();
                    this.fullTextType = response.FullTextType === 0 ? "HTML" : "XML";
                    this.setupHeaderControls();
                    resolve();
                });
        } else {
                const htmlMessage = Mustache.render(restrictedReport, {Title: this.model.Title, IsAuthenticated: this.authService.AppUser.IsAuthenticated });
                this.unloadReportHolder();
                this.loadReportHolder(htmlMessage);
                if (this.model.Data.DocumentAccessSku.toLowerCase() !== "free") {
                    const url = window.location.href;
                    this.logReportInterest(reportSource, url);
                }
                resolve();
            }
        });
    }

    private loadReportPDF(pdfLink: string, source: string, index: string, name: string) {
        let pdfUrl;
        const domainName = this.getDomainName(AppSettings.DocumentApiUrlPrefix);

        if (pdfLink && pdfLink !== "null") {
            pdfLink = encodeURIComponent(pdfLink);
            pdfUrl = `${domainName}${AppSettings.DocumentApiUrlPrefix}reportpdf/?pdfLink=${pdfLink}`;
        } else {
            pdfUrl = `${domainName}${AppSettings.DocumentApiUrlPrefix}reportpdf/generate/${this.model.Data.Id}/${source}/${index}`;
        }

        if (_.contains(this.authService.AppUser.Indicators, source)) {
            pdfUrl = encodeURIComponent(pdfUrl);
            const readerUrl = `assets/pdf-reader/web/viewer.html?file=${pdfUrl}&v=${AppSettings.AppVersion}`;
            const existing = this.$(".report-holder iframe").attr("src");
            if (existing !== readerUrl) {
                const frame = $(`<iframe src='${readerUrl}' class='pdf-frame' frameborder="0"></iframe>`);
                this.unloadReportHolder();
                this.loadReportHolder(frame);
                this.setupIEBugFix($(frame).get(0));
                this.setButtonStatusForPdfDisplay();
            }
        } else {
            const htmlMessage = Mustache.render(restrictedReport, {Title: name, IsAuthenticated: this.authService.AppUser.IsAuthenticated });
            this.unloadReportHolder();
            this.loadReportHolder(htmlMessage);
            if (this.model.Data.DocumentAccessSku.toLowerCase() !== "free") {
                const url = window.location.href;
                this.logReportInterest(source, url);
            }
        }
        return Promise.resolve({});
    }

    private async reportSelectClick(e: JQueryMouseEventObject) {
        const $button = $(e.currentTarget);
        if (this.model.State.FullTextMode === FullTextMode.Text) {
            this.currentSource = $button.attr("data-source");
            this.currentIndex = parseInt($button.attr("data-index"));
            await this.loadReportText();
            this.model.State.Highlighting = false; // Reset highlighting when changing between reports.
        } else {
            this.switchToThisPdf($button);
        }
    }

    private fullTextModeChanged() {
        this.updateControls();

        if (this.model.State.FullTextMode === FullTextMode.Pdf) {
            this.displayReportPdf(this.model.ReportDetails);
        } else {
            this.loadReportText().then(() => {
                this.model.State.Highlighting = true;
            });
        }
    }

    private updateControls() {
        const mode = this.model.State.FullTextMode;
        const content = this.$(".full-text-content.scrollable");
        const fullText = this.$(".full-text-document");
        const pdfHolder = this.$(".full-text-pdf");
        const paragraphControls = this.$(".paragraph-controls");
        const highlightControls = this.$(".highlight-controls");
        const pdfButton = this.$(".show-pdf");
        const textButton = this.$(".show-text");
        const pdfControls = this.$(".document-pdf-selectors");
        const scrollControls = this.$(".judgment-scroll-selectors");
        const headerContentControls = this.$(".header-content-controls");

        if (mode === FullTextMode.Pdf) {
            fullText.hide().removeClass("show");
            pdfControls.show();
            highlightControls.hide();
            paragraphControls.hide();
            scrollControls.hide();
            headerContentControls.hide();
            textButton.show();
            pdfButton.hide();
            pdfHolder.removeClass("report-pdf").addClass("document-pdf").removeClass("hide").show();
            this.$(content).addClass("pdf-mode");
        } else {
            fullText.show();
            pdfControls.hide();
            highlightControls.show();
            paragraphControls.hide();
            scrollControls.show();
            this.model.State.ShowHeaderControls ? headerContentControls.show() : headerContentControls.hide();
            pdfHolder.hide().addClass("hide");
            textButton.hide();
            pdfButton.show();
            this.$(content).removeClass("pdf-mode");
        }
    }

    private documentPDFClick(e: JQueryEventObject): void {
        $(e.currentTarget).blur();
        e.stopPropagation();
        if ($(e.currentTarget).hasClass("disabled")) {
            return;
        }

        if (this.model.State.FullTextMode === FullTextMode.Text) {
            this.switchToPdfViewMode();
        } else {
            this.switchToTextViewMode();
        }
    }

    private toggleHeaderContent(): void {
        this.model.State.IsHeaderHidden = !this.model.State.IsHeaderHidden;
        const isHeaderHidden = this.model.State.IsHeaderHidden;
        const fullText = this.$(dom.FULL_TEXT_CONTENT);
        const headerContent = fullText.find(".justis-doc").children().nextUntil(".judgment");
        if (isHeaderHidden) {
            headerContent.hide();
            this.$(dom.TOGGLE_HEADER_CONTENT).html(`<i class="fa fa-file-text" aria-hidden="true"></i> &nbsp;Show Header`);
            if (this.model.State.Highlighting) {
                if (headerContent.find(".text-highlight.current-text-highlight-term").length > 0) {
                    this.moveSearchTermFocus(1);
                }
            }
        } else {
            headerContent.show();
            this.$(dom.TOGGLE_HEADER_CONTENT).html(`<i class="fa fa-file-text" aria-hidden="true"></i> &nbsp;Hide Header`);
        }
        this.$(dom.TOGGLE_HEADER_CONTENT).blur();
    }

    private activeTabChanged(): any {
        if (this.model.Active) {
            if (this.model.State.FullTextMode === FullTextMode.Text) {
                this.loadReportText().then(() => {
                    this.model.State.Highlighting = true;
                });

                _.delay(() => {
                    if (window.opener) {
                        window.opener.postMessage({message: "ReportFullScreenRequest"}, this.newTab);
                    }
                }, 1000);
            } else {
                this.displayReportPdf(this.model.ReportDetails);
            }
            this.updateControls();
        }
    }

    private loadReportHolder(item: any) {
        this.$(dom.REPORT_HOLDER).append(item);
    }

    private unloadReportHolder() {
        this.$(dom.REPORT_HOLDER).empty();
    }

    private setupHighlighter() {
        let foundTerms = false;
        const sanitisedTermsList = TermSanitiser.getSanitisedTermsList(this.model.Data.HighlightableSearchTerms);
        if (sanitisedTermsList && sanitisedTermsList.length > 0) {
            foundTerms = this.$(dom.FULL_TEXT_CONTENT).hasMatches(sanitisedTermsList, { wordsOnly: true });
        }

        if (!foundTerms) {
            this.$(dom.HIGHLIGHT_BUTTON).addClass("disabled");
        }

        this.updateSearchTermNavigationButtonStatus(foundTerms);
    }

    private highlightingChanged(): void {
        const doHighlighting = this.model.State.Highlighting;
        this.$(dom.HIGHLIGHT_BUTTON).toggleClass("active", doHighlighting);
        if (doHighlighting) {
            const sanitisedTermsList = TermSanitiser.getSanitisedTermsList(this.model.Data.HighlightableSearchTerms);
            if (sanitisedTermsList.length > 0) {
                this.$(dom.FULL_TEXT_CONTENT).highlight(sanitisedTermsList, { wordsOnly: true });
                this.$(dom.FULL_TEXT_CONTENT).find(".text-highlight:first").addClass("current-text-highlight-term");
            } else {
                this.model.State.Highlighting = false;
            }
        } else {
            this.$(dom.FULL_TEXT_CONTENT).unhighlight();
        }
        _.defer(() => {
            this.setupHighlighter();
        });
    }

    private highlightClick(e: JQueryEventObject): void {
        e.stopPropagation();
        const currentState = !this.model.State.Highlighting;
        this.model.State.Highlighting = currentState;
        const $button = $(e.currentTarget);

        currentState ? $button.addClass("active") : $button.removeClass("active");

        $button.blur();
        if ($button.hasClass("active")) {
            this.model.State.HighlightedTerms = this.$(dom.HIGHLIGHTED_TEXT).get();
        }
        this.updateSearchTermNavigationButtonStatus(currentState);

        if (this.model.State.HighlightedTerms.length > 0) {
            const searchTerms = TermSanitiser.getSanitisedTermsList(this.model.Data.HighlightableSearchTerms).toString();
            this.appEvents.trigger(AppEvents.AppInsightsDocumentHighlightTerm, {Highlighting: currentState, SearchTerms: searchTerms});
        }
    }

    private searchTermNextClick(e: JQueryEventObject): void {
        e.stopPropagation();
        if (!this.$(dom.SEARCH_TERM_NEXT).hasClass("disabled")) {
            this.moveSearchTermFocus(1); // 1= next; -1; previous
            this.updateSearchTermNavigationButtonStatus(true);
        }
    }

    private searchTermPrevClick(e: JQueryEventObject): void {
        e.stopPropagation();
        if (!this.$(dom.SEARCH_TERM_PREV).hasClass("disabled")) {
            this.moveSearchTermFocus(-1); // 1= next; -1; previous
            this.updateSearchTermNavigationButtonStatus(true);
        }
    }

    private moveSearchTermFocus(direction: number): void {
        const highlightedTerms = this.$(dom.HIGHLIGHTED_TEXT);
        const maxTermPos = highlightedTerms.length - 1;
        const currentTermPosn = this.$(highlightedTerms).index(this.$(dom.CURRENT_HIGHLIGHTED_TERM));
        if (currentTermPosn === 0 && direction === -1) {
            return;
        }

        if (currentTermPosn === -1) {
            highlightedTerms.first().addClass("current-text-highlight-term");
        } else {
            let newPosn: number = currentTermPosn;
            if (this.model.State.IsHeaderHidden) {
                if (direction === 1) {
                    for (let i = currentTermPosn + 1; i < highlightedTerms.length; i++) {
                        const elem = this.$(highlightedTerms).get(i);
                        if (this.$(elem).parents(".judgment").length > 0) {
                            newPosn = i;
                            break;
                        }
                    }
                } else {
                    for (let i = currentTermPosn - 1; i >= 0; i--) {
                        const elem = this.$(highlightedTerms).get(i);
                        if (this.$(elem).parents(".judgment").length > 0) {
                            newPosn = i;
                            break;
                        }
                    }
                }
            } else {
                newPosn = (direction === 1) ? currentTermPosn + 1 : currentTermPosn - 1;
            }

            if (newPosn > maxTermPos) {
                newPosn -= 1;
            }

            this.$(highlightedTerms.get(currentTermPosn)).removeClass("current-text-highlight-term");
            this.$(highlightedTerms.get(newPosn)).addClass("current-text-highlight-term");
        }

        this.fullTextScroll(this.$(dom.CURRENT_HIGHLIGHTED_TERM), 200, -40);
    }

    private fullTextScroll(selector, duration = 800, offsetTop = -45, easing = "easeInOutExpo") {
        _.delay(() => {
            this.$(dom.FULL_TEXT_CONTENT).scrollTo(selector, duration, { easing, offset: { top: offsetTop, left: 0 }, axis: "y" });
        }, 550);
    }

    private toggleDisabledStateOfControl(e: JQuery, disabled: boolean): void {
        e.toggleClass("disabled", disabled).attr("aria-disabled", disabled.toString());
    }

    private updateSearchTermNavigationButtonStatus(active: boolean) {
        this.toggleDisabledStateOfControl(this.$(dom.SEARCH_TERM_PREV), !active);
        this.toggleDisabledStateOfControl(this.$(dom.SEARCH_TERM_NEXT), !active);
    }

    private switchToTextViewMode(): any {
        this.model.State.FullTextMode = FullTextMode.Text;
    }

    private switchToPdfViewMode(): any {
        this.model.State.FullTextMode = FullTextMode.Pdf;
    }

    private logReportInterest(source: string, docUrl: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            $.ajax({
                beforeSend: AuthorizationModel.attachAuthorizationHeader,
                type: "POST",
                cache: false,
                url: `${AppSettings.ApiUrlPrefix}logging/reportinterest`,
                data: {
                         source,
                        url: docUrl
                    }
            }).done(resolve).fail(reject);
        });
    }

    private setupIEBugFix(iframe) {
        iframe.onblur = () => {
            window.focus();
        };
    }

    private displayReportPdf(reports: ReportDetailModel[]) {
        const $reportGroup = this.setupReportsDropdown(reports);
        this.switchToThisPdf($reportGroup.find(dom.REPORT_PDF_BUTTON).first());
    }

    private setupReportsDropdown(reports: ReportDetailModel[]) {
        const $reportGroup = this.$(".report-select");
        $reportGroup.empty();
        if (reports.length > 1) {
            const reportButton = `<button class="btn dropdown-toggle" data-toggle='dropdown'><i class="fa"></i>Reports <span class="caret"></span></button>`;
            $reportGroup.append(reportButton);
            $reportGroup.append("<ul class='dropdown-menu'></ul>");
            for (let i = 0; i < reports.length; i++) {
                const report = reports[i];
                if (report.Reference === "Report") {
                    report.Reference = report.Reference + " " + (i + 1);
                }
                const $report = $(`<li class='report-button' data-name='${report.ReportName}' data-source='${report.Source}' data-id='${this.model.Data.Id}' data-index='${i}' data-pdf='${report.PdfLink}'>${report.Reference}</li>`);
                $reportGroup.find(".dropdown-menu").append($report);
            }
        }
        else {
            const $report = $(`<button class='btn report-button disabled' data-name='${reports[0].ReportName}' data-source='${reports[0].Source}' data-id='${this.model.Data.Id}' data-index='0' data-pdf='${reports[0].PdfLink}'>${reports[0].Reference}</button>`);
            $reportGroup.append($report);
        }
        return $reportGroup;
    }

    private switchToThisPdf(button: any) {
        const link = button.attr("data-pdf");
        const source = button.attr("data-source");
        const index = button.attr("data-index");
        const name = button.attr("data-name");
        this.loadReportPDF(link, source, index, name);
    }

    private getDomainName(apiUrl) {
        if (apiUrl.substr(0, 2) !== "//") {
            let protocolAddition = window.location.protocol + "//" + window.location.hostname;

            if (window.location.port) {
                protocolAddition += ":" + window.location.port;
            }

            protocolAddition += "/";
            return protocolAddition;
        }

        return "";
    }

    private fullscreenReaderButtonClick(e) {
        _.defer(() => {
            const visible = this.$(".fulltext-section:visible");
            const clone = visible.clone();
            this.appEvents.trigger("FullScreen", clone);
        });
    }

    private fullscreenReaderNewTabButtonClick(e) {
        this.appEvents.trigger(AppEvents.AppInsightsDocumentReadMode, {RenderType: this.model.Data.RenderType});
        this.newTab = window.open(Backbone.history.getFragment());
        this.newTab.focus();
    }

    private receieveMessage(event) {
        if (event.origin !== AppSettings.WindowOrigin) {
            return;
        }

        if (event.data.message === "ReportFullScreen") {
            this.model.State.FullTextMode = event.data.fullTextMode;
            _.delay(() => {
                const visible = this.$(".fulltext-section:visible");
                const clone = visible.clone();
                this.appEvents.trigger("FullScreen", clone);
                return;
            }, 1000);
        }

        if (event.data.message === "ReportFullScreenRequest" && this.newTab) {
            this.newTab.postMessage({message: "ReportFullScreen", fullTextMode: this.model.State.FullTextMode}, window.opener);
            this.newTab = null;
            return;
        }
    }

    private setButtonStatusForTextDisplay() {
        const numPDFs = this.model.ReportDetails.length;
        if (numPDFs > 0) {
            this.setupReportsDropdown(this.model.ReportDetails);
            this.$(dom.DOCUMENT_PDF_BUTTON).removeClass("disabled").attr("title", "View the document's PDF");
        }
    }

    private setButtonStatusForPdfDisplay() {
        const numPDFs = this.model.ReportDetails.length;
        if (numPDFs > 0) {
            this.setupReportsDropdown(this.model.ReportDetails);
            this.$(dom.DOCUMENT_PDF_BUTTON).removeClass("disabled").attr("title", "View the document's Text");
        }
    }

    private loadCSS(href: string) {
        dev.log(`CSS file: ${href}`);
        const cssLink = $("<link>");
        $("head").append(cssLink); // IE hack: append before setting href

        cssLink.attr({
            rel:  "stylesheet",
            type: "text/css",
            href: href
        });
    }

    private setupHeaderControls() {
        if (this.fullTextType === "HTML") {
            this.$(dom.HEADER_CONTENT_CONTROLS).hide();
            this.model.State.ShowHeaderControls = false;
        } else {
            this.$(dom.HEADER_CONTENT_CONTROLS).show();
            this.model.State.ShowHeaderControls = true;
        }
    }

    private toggleReadModeDropdown() {
        this.$("#read-mode").toggle();

        $(window.document).click(() => {
            this.closeReadModeDropdown();
        });
    }

    private closeReadModeDropdown() {
        this.$("#read-mode").hide();
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/tabs/report.ts